import Cart from 'cart-store';
import { formatMoney } from 'utils';

class FreeShippingBar extends HTMLElement {
  // 现有私有属性
  #animationInterval = null;
  #modalHandler;
  #modalElement;

  // 新增私有属性用于多门槛支持
  #tiers = [];
  #currentTierIndex = -1;
  #tierMarkers = [];
  #lastAnimatedTierIndex = -1; // 跟踪最后播放动画的门槛索引

  constructor() {
    super();
    this.#modalHandler = this.#handleModalEvents.bind(this);
  }

  async connectedCallback() {
    try {
      // 初始化DOM元素引用
      this.amountToSpendElement = this.querySelector('amount-to-spend');
      this.amountLeftElement = this.querySelector('amount-left');
      this.moneyValueLeftElement = this.amountLeftElement?.querySelector('money-value');
      this.minimumReachedElement = this.querySelector('minimum-reached');
      this.track = this.querySelector('free-shipping-bar-track');
      this.goalAnimation = null;

      // 验证必需的DOM元素
      if (!this.track) {
        console.error('Required element free-shipping-bar-track not found');
        return;
      }

      // 获取基本配置，确保数值有效
      this.minimumValue = Math.max(0, Number(this.getAttribute('minimum-value')) || 0);
      this.value = Math.max(0, Number(this.getAttribute('value')) || 0);

      // 解析门槛配置
      this.#parseTiers();

      // 查找模态框父元素
      this.#modalElement = this.closest('modal-dialog, popup-dialog');

      // 初始化计算
      this.#calculateProgress();

      // 初始化门槛标记点位置
      this.#updateTierMarkers();
      this.#updateDisplay()
      // 订阅购物车变化
      this.unsubscribe = Cart.subscribe(state => {
        if (this.value !== state.cart.total_price) {
          const previousValue = this.value;
          this.value = state.cart.total_price;
          this.#updateDisplay();

          // 检测新达成的门槛并触发动画
          this.#checkAndPlayTierAnimations(previousValue);
        }
      });

      // 设置显示延迟
      this.revealDelay = this.getAttribute('reveal-delay') || 0;

      // 处理模态框事件
      if (this.#modalElement) {
        this.#modalElement.addEventListener('on:modal:opened', this.#modalHandler);
        this.#modalElement.addEventListener('on:modal:closed', this.#modalHandler);
      } else {
        // 设置 Intersection Observer
        const observerOptions = {
          rootMargin: '0px',
          threshold: 0.95,
        };

        this.visibilityObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            setTimeout(() => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                setTimeout(() => {
                  if (this.amountLeft === 0) {
                    this.#playGoalAnimation();
                  }
                }, 500);
                this.visibilityObserver.disconnect();
              }
            }, this.revealDelay);
          });
        }, observerOptions);

        this.visibilityObserver.observe(this);
      }

      // 初始化动画
      const animationElement = this.querySelector('free-shipping-bar-goal-animation');
      if (animationElement) {
        try {
          const { DotLottie } = await import('dotlottie');
          const initAnimation = () => {
            this.goalAnimation = new DotLottie({
              canvas: animationElement.querySelector('canvas'),
              loop: false,
              autoplay: false,
              src: animationElement.getAttribute('data-url'),
            });
          };

          if (this.#modalElement) {
            this.#modalElement.on('on:modal:opening', initAnimation, { once: true });
          } else {
            initAnimation();
          }
        } catch (error) {
          console.warn('Failed to initialize animation:', error);
          // 动画初始化失败时继续运行，只是没有动画效果
        }
      }
    } catch (error) {
      console.error('Error in connectedCallback:', error);
      // 即使出错也要确保基本功能可用
      this.#fallbackToDefault();
    }
  }

  disconnectedCallback() {
    this.classList.remove('is-visible');

    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }

    if (this.#modalElement) {
      this.#modalElement.removeEventListener('on:modal:opened', this.#modalHandler);
      this.#modalElement.removeEventListener('on:modal:closed', this.#modalHandler);
    }

    if (this.unsubscribe) {
      this.unsubscribe();
    }

    if (this.#animationInterval) {
      clearInterval(this.#animationInterval);
      this.#animationInterval = null;
    }

    // 清理动画状态
    this.removeAttribute('data-animation-playing');
  }

  // 新增：解析门槛配置
  #parseTiers() {
    try {
      const tiersAttribute = this.getAttribute('tiers');

      if (tiersAttribute) {
        const parsedTiers = JSON.parse(tiersAttribute.trim());

        // 验证门槛配置数组
        if (Array.isArray(parsedTiers)) {
          const validTiers = [];

          for (const tier of parsedTiers) {
            // 验证必需字段
            if (!tier.amount || typeof tier.amount !== 'number' || tier.amount <= 0) {
              console.warn('Invalid tier amount:', tier);
              continue;
            }

            if (!tier.type || typeof tier.type !== 'string') {
              console.warn('Invalid tier type:', tier);
              continue;
            }

            if (!tier.title || typeof tier.title !== 'string') {
              console.warn('Invalid tier title:', tier);
              continue;
            }

            // 创建验证后的门槛对象，设置默认值
            const validTier = {
              amount: tier.amount,
              type: tier.type,
              title: tier.title,
              icon: tier.icon || this.#getDefaultIcon(tier.type),
              animation: tier.animation || null,
            };

            validTiers.push(validTier);
          }

          // 按金额升序排序
          this.#tiers = validTiers.sort((a, b) => a.amount - b.amount);

          // 验证门槛金额不能重复
          const amounts = this.#tiers.map(tier => tier.amount);
          const uniqueAmounts = [...new Set(amounts)];
          if (amounts.length !== uniqueAmounts.length) {
            console.warn('Duplicate tier amounts detected, removing duplicates');
            const uniqueTiers = [];
            const seenAmounts = new Set();

            for (const tier of this.#tiers) {
              if (!seenAmounts.has(tier.amount)) {
                seenAmounts.add(tier.amount);
                uniqueTiers.push(tier);
              }
            }
            this.#tiers = uniqueTiers;
          }
        }
      }

      // 如果没有有效的门槛配置，使用默认的免费配送门槛
      if (this.#tiers.length === 0 && this.minimumValue > 0) {
        this.#tiers = [
          {
            amount: this.minimumValue,
            type: 'free_shipping',
            title: '免费配送',
            icon: 'truck',
            animation: null,
          },
        ];
      }
    } catch (error) {
      console.warn('Failed to parse tiers configuration, falling back to default:', error);
      // 回退到默认免费配送模式
      this.#fallbackToDefault();
    }
  }

  // 新增：获取默认图标
  #getDefaultIcon(type) {
    const iconMap = {
      free_shipping: 'truck',
      gift_small: 'gift',
      gift_large: 'gift-large',
      discount: 'percent',
      bonus: 'star',
    };
    return iconMap[type] || 'gift';
  }

  // 新增：回退到默认配置
  #fallbackToDefault() {
    if (this.minimumValue > 0) {
      this.#tiers = [
        {
          amount: this.minimumValue,
          type: 'free_shipping',
          title: '免费配送',
          icon: 'truck',
          animation: null,
        },
      ];
    } else {
      this.#tiers = [];
    }
  }

  // 保持原有的计算逻辑，扩展支持多门槛
  #calculateProgress() {
    try {
      // 边界条件：确保 value 不为负数
      this.value = Math.max(0, this.value || 0);

      // 如果没有门槛配置，使用原始逻辑
      if (this.#tiers.length === 0) {
        // 边界条件：确保 minimumValue 有效
        const safeMinimumValue = Math.max(0, this.minimumValue || 0);
        this.amountLeft = safeMinimumValue - this.value <= 0 ? 0 : safeMinimumValue - this.value;

        try {
          this.moneyLeft = formatMoney(
            this.amountLeft,
            window.theme?.money_format || '${{amount}}',
          ).replace(/[.,]00$/, '');
        } catch (error) {
          console.warn('Money formatting failed, using fallback:', error);
          this.moneyLeft = `${(this.amountLeft / 100).toFixed(2)}`;
        }

        // 使用与多门槛相同的计算逻辑，确保一致性
        this.displacement =
          safeMinimumValue > 0
            ? Math.round(((safeMinimumValue - this.value) / safeMinimumValue) * 100)
            : 0;
        return;
      }
    } catch (error) {
      console.error('Error in calculateProgress (fallback mode):', error);
      // 降级到最基本的显示
      this.amountLeft = 0;
      this.moneyLeft = '$0.00';
      this.displacement = 0;
      return;
    }

    // 计算当前门槛索引
    this.#currentTierIndex = -1;
    for (let i = 0; i < this.#tiers.length; i++) {
      if (this.value >= this.#tiers[i].amount) {
        this.#currentTierIndex = i;
      } else {
        break;
      }
    }

    // 计算下一个门槛
    const nextTierIndex = this.#currentTierIndex + 1;
    const maxTierAmount = this.#tiers[this.#tiers.length - 1].amount;

    if (nextTierIndex < this.#tiers.length) {
      // 还有下一个门槛
      const nextTier = this.#tiers[nextTierIndex];
      this.amountLeft = nextTier.amount - this.value;
      this.nextTierAmount = nextTier.amount;
      this.nextTierTitle = nextTier.title;
      this.nextTierType = nextTier.type;
    } else {
      // 已达到最高门槛
      this.amountLeft = 0;
      this.nextTierAmount = maxTierAmount;
      this.nextTierTitle = this.#tiers[this.#tiers.length - 1].title;
      this.nextTierType = this.#tiers[this.#tiers.length - 1].type;
    }

    // 格式化剩余金额
    this.moneyLeft = formatMoney(this.amountLeft, window.theme.money_format).replace(/[.,]00$/, '');
    // 计算整体进度百分比（基于最高门槛）
    this.displacement = Math.round(((maxTierAmount - this.value) / maxTierAmount) * 100);

    // 确保进度不会超过100%或低于0%
    this.displacement = Math.max(0, Math.min(100, this.displacement));

    // 计算当前门槛段的进度百分比
    if (this.#currentTierIndex >= 0 && nextTierIndex < this.#tiers.length) {
      const currentTier = this.#tiers[this.#currentTierIndex];
      const nextTier = this.#tiers[nextTierIndex];
      const tierRange = nextTier.amount - currentTier.amount;
      const tierProgress = this.value - currentTier.amount;
      this.tierProgressPercentage = Math.round((tierProgress / tierRange) * 100);
    } else if (this.#currentTierIndex === -1 && this.#tiers.length > 0) {
      // 还没达到第一个门槛
      const firstTier = this.#tiers[0];
      this.tierProgressPercentage = Math.round((this.value / firstTier.amount) * 100);
    } else {
      // 已达到最高门槛
      this.tierProgressPercentage = 100;
    }

    // 为向后兼容，保持原有的 minimumValue 逻辑
    if (this.#tiers.length > 0) {
      this.minimumValue = this.#tiers[0].amount;
    }
  }

  // 保持原有的UI更新逻辑，扩展支持多门槛显示
  #updateDisplay() {
    this.setAttribute('value', this.value);
    this.#calculateProgress();

    if (this.moneyValueLeftElement) {
      this.moneyValueLeftElement.innerHTML = this.moneyLeft;
    }

    // 更新文本显示状态 - 支持多门槛
    this.#updateTextElements();

    // 更新进度条显示
    this.track.style.setProperty('--displacement', `${this.displacement}%`);

    // 新增：更新门槛标记点状态
    this.#updateTierMarkers();
  }

  // 新增：更新文本元素显示状态
  #updateTextElements() {
    // 如果没有门槛配置，使用原始逻辑
    if (this.#tiers.length === 0) {
      if (this.value > 0) {
        this.amountToSpendElement.setAttribute('hidden', '');
      } else {
        this.amountToSpendElement.removeAttribute('hidden');
      }

      if (this.value === 0 || this.amountLeft === 0) {
        this.amountLeftElement.setAttribute('hidden', '');
      } else {
        this.amountLeftElement.removeAttribute('hidden');
      }

      if (this.amountLeft > 0) {
        this.minimumReachedElement.setAttribute('hidden', '');
      } else {
        this.minimumReachedElement.removeAttribute('hidden');
      }
      return;
    }

    // 多门槛逻辑
    const hasReachedAnyTier = this.#currentTierIndex >= 0;
    const hasReachedAllTiers = this.#currentTierIndex === this.#tiers.length - 1;

    // amount-to-spend: 显示在购物车为空时
    if (this.value === 0) {
      this.amountToSpendElement.removeAttribute('hidden');
      // 更新文本内容为第一个门槛
      if (this.#tiers.length > 0) {
        const firstTier = this.#tiers[0];
        const firstTierAmount = formatMoney(firstTier.amount, window.theme.money_format).replace(
          /[.,]00$/,
          '',
        );
        this.#updateElementText(
          this.amountToSpendElement,
          `${firstTier.title}まであと ${firstTierAmount}`,
        );
      }
    } else {
      this.amountToSpendElement.setAttribute('hidden', '');
    }

    // amount-left: 显示距离下一个门槛的金额
    if (this.value > 0 && this.amountLeft > 0) {
      this.amountLeftElement.removeAttribute('hidden');
      // 更新文本内容为下一个门槛信息
      if (this.nextTierTitle) {
        this.#updateElementText(
          this.amountLeftElement,
          `${this.nextTierTitle}まであと ${this.moneyLeft}`,
        );
      }
    } else {
      this.amountLeftElement.setAttribute('hidden', '');
    }

    // minimum-reached: 显示已达到的门槛状态
    if (hasReachedAllTiers) {
      this.minimumReachedElement.removeAttribute('hidden');
      // 更新文本内容为所有奖励已解锁
      // this.#updateElementText(this.minimumReachedElement, 'ご購入金額が条件を満たしました。送料無料となりました。プレミアムギフトセット（5点入り＋日傘1点）をプレゼント中');
      // this.#updateElementText(this.minimumReachedElement, 'ホリデーギフトセットをプレゼント中');
      // this.#updateElementText(this.minimumReachedElement, 'ゴールデンウィーク限定ギフト配布中');
      // this.#updateElementText(this.minimumReachedElement, '夏のUV対策限定特典プレゼント中');
       const goalReachedMessage = this.getAttribute('goal-reached-message') || '夏のUV対策限定特典プレゼント中';
      this.#updateElementText(this.minimumReachedElement, goalReachedMessage);
    } else if (hasReachedAnyTier && this.amountLeft === 0) {
      this.minimumReachedElement.removeAttribute('hidden');
      // 更新文本内容为当前达到的门槛
      const currentTier = this.#tiers[this.#currentTierIndex];
      this.#updateElementText(this.minimumReachedElement, `🎉 恭喜获得${currentTier.title}！`);
    } else {
      this.minimumReachedElement.setAttribute('hidden', '');
    }
  }

  // 新增：安全更新元素文本内容
  #updateElementText(element, text) {
    if (element && element.textContent !== text) {
      // 保留原有的 HTML 结构，只更新文本内容
      const textNode = element.querySelector('span') || element;
      if (textNode.tagName === 'SPAN' || !element.querySelector('span')) {
        textNode.textContent = text;
      }
    }
  }

  // 新增：更新门槛标记点的显示状态
  #updateTierMarkers() {
    try {
      // 获取所有门槛标记点
      const tierMarkers = this.track?.querySelectorAll('tier-marker');

      if (!tierMarkers || tierMarkers.length === 0 || this.#tiers.length === 0) {
        return;
      }

      // 计算最高门槛金额用于位置计算
      const maxTierAmount = this.#tiers[this.#tiers.length - 1].amount;

      if (!maxTierAmount || maxTierAmount <= 0) {
        console.warn('Invalid max tier amount:', maxTierAmount);
        return;
      }

      tierMarkers.forEach(marker => {
        try {
          const tierAmount = Number(marker.getAttribute('data-amount'));

          // 验证门槛数据
          if (!tierAmount || tierAmount <= 0) {
            console.warn('Invalid tier amount for marker:', marker);
            return;
          }

          // 计算标记点在进度条上的位置（百分比）
          // 门槛标记点的位置是固定的，基于门槛金额在最大门槛中的比例
          const position = Math.min(100, Math.max(0, (tierAmount / maxTierAmount) * 100));
          marker.style.left = `${position}%`;

          // 移除所有状态类
          marker.classList.remove('tier-marker--achieved', 'tier-marker--current');

          // 根据当前购物车金额设置标记点状态
          if (this.value >= tierAmount) {
            // 已达成的门槛
            marker.classList.add('tier-marker--achieved');
          } else if (
            this.#currentTierIndex + 1 < this.#tiers.length &&
            this.#tiers[this.#currentTierIndex + 1].amount === tierAmount
          ) {
            // 下一个要达成的门槛
            marker.classList.add('tier-marker--current');
          }

          // 更新工具提示内容
          const tierTitle = marker.getAttribute('data-title') || '未知奖励';

          try {
            const formattedAmount = formatMoney(
              tierAmount,
              window.theme?.money_format || '${{amount}}',
            ).replace(/[.,]00$/, '');

            if (this.value >= tierAmount) {
              marker.setAttribute('data-title', `✅ ${tierTitle} - ${formattedAmount}`);
            } else {
              const remaining = Math.max(0, tierAmount - this.value);
              const formattedRemaining = formatMoney(
                remaining,
                window.theme?.money_format || '${{amount}}',
              ).replace(/[.,]00$/, '');
              marker.setAttribute('data-title', `${tierTitle} - 还需 ${formattedRemaining}`);
            }
          } catch (formatError) {
            console.warn('Money formatting failed for tier marker:', formatError);
            // 使用简单的格式化作为备选
            const simpleAmount = `${(tierAmount / 100).toFixed(2)}`;
            marker.setAttribute('data-title', `${tierTitle} - ${simpleAmount}`);
          }
        } catch (markerError) {
          console.warn('Error updating tier marker:', markerError, marker);
        }
      });

      // 缓存标记点引用
      this.#tierMarkers = Array.from(tierMarkers);
    } catch (error) {
      console.error('Error in updateTierMarkers:', error);
    }
  }

  // 扩展的动画逻辑，支持多门槛庆祝动画
  #playGoalAnimation(tierType = 'free_shipping') {
    try {
      const animationElement = this.querySelector('free-shipping-bar-goal-animation');

      if (!animationElement) {
        console.warn('Animation element not found, skipping animation');
        return;
      }

      // 检查门槛达成状态
      const achievedTiers = this.#checkTierAchievements();
      if (!achievedTiers.includes(tierType)) {
        console.warn(`Tier ${tierType} not achieved, skipping animation`);
        return;
      }

      // 防止同时播放多个动画
      if (this.#animationInterval) {
        clearInterval(this.#animationInterval);
        this.#animationInterval = null;
      }

      // 检查是否已有动画在播放
      if (this.getAttribute('data-animation-playing')) {
        console.log('Animation already playing, skipping');
        return;
      }

      // 添加动画播放状态标记
      this.setAttribute('data-animation-playing', tierType);

      this.#animationInterval = setInterval(() => {
        try {
          if (this.goalAnimation && this.goalAnimation.isLoaded) {
            clearInterval(this.#animationInterval);
            this.#animationInterval = null;

            const canvas = this.querySelector('free-shipping-bar-goal-animation canvas');
            if (canvas && canvas.getAttribute('width') === '0') {
              canvas.setAttribute('width', 300);
              canvas.setAttribute('height', 300);
            }

            // 播放动画
            this.goalAnimation.play();

            // 设置动画完成后的清理
            this.#setupAnimationCleanup();
          }
        } catch (intervalError) {
          console.error('Error in animation interval:', intervalError);
          clearInterval(this.#animationInterval);
          this.#animationInterval = null;
          this.removeAttribute('data-animation-playing');
        }
      }, 100);
    } catch (error) {
      console.error('Error in playGoalAnimation:', error);
      // 确保动画错误不影响其他功能
      this.removeAttribute('data-animation-playing');
      if (this.#animationInterval) {
        clearInterval(this.#animationInterval);
        this.#animationInterval = null;
      }
    }
  }

  // 新增：设置动画完成后的清理
  #setupAnimationCleanup() {
    try {
      // 监听动画完成事件
      const handleAnimationComplete = () => {
        this.removeAttribute('data-animation-playing');
      };

      // 尝试监听动画完成事件
      if (this.goalAnimation && typeof this.goalAnimation.addEventListener === 'function') {
        this.goalAnimation.addEventListener('complete', handleAnimationComplete, { once: true });
      } else if (this.goalAnimation && typeof this.goalAnimation.onComplete === 'function') {
        // 备用事件监听方式
        this.goalAnimation.onComplete = handleAnimationComplete;
      }

      // 备用清理机制（防止事件未触发）
      setTimeout(() => {
        if (this.getAttribute('data-animation-playing')) {
          console.warn('Animation cleanup timeout triggered, forcing state cleanup');
          this.removeAttribute('data-animation-playing');
        }
      }, 3000); // 3秒后强制清理
    } catch (error) {
      console.error('Error in animation cleanup setup:', error);
      // 确保即使出错也能清理状态
      setTimeout(() => {
        this.removeAttribute('data-animation-playing');
      }, 1000);
    }
  }

  // 新增：检查门槛达成状态
  #checkTierAchievements() {
    const achievedTiers = [];

    for (const tier of this.#tiers) {
      if (this.value >= tier.amount) {
        achievedTiers.push(tier.type);
      }
    }

    return achievedTiers;
  }

  // 新增：检测新达成的门槛并触发动画
  #checkAndPlayTierAnimations(previousValue) {
    try {
      if (this.#tiers.length === 0) {
        console.log('No tiers configured, skipping animation check');
        return;
      }

      // 验证输入参数
      const safePreviousValue = Math.max(0, previousValue || 0);
      const safeCurrentValue = Math.max(0, this.value || 0);

      // 确保价格是增加的，避免价格减少时触发动画
      if (safeCurrentValue <= safePreviousValue) {
        return;
      }

      // 收集所有新达成的门槛
      const newlyAchievedTiers = [];

      for (const tier of this.#tiers) {
        if (!tier || typeof tier.amount !== 'number' || tier.amount <= 0) {
          console.warn('Invalid tier in animation check:', tier);
          continue;
        }

        const wasAchieved = safePreviousValue >= tier.amount;
        const isNowAchieved = safeCurrentValue >= tier.amount;

        // 如果是新达成的门槛，添加到列表
        if (!wasAchieved && isNowAchieved) {
          newlyAchievedTiers.push(tier);
        }
      }

      // 如果跨越多个门槛，只播放最高门槛的动画
      if (newlyAchievedTiers.length > 0) {
        // 按金额排序，选择最高的门槛
        const highestTier = newlyAchievedTiers.sort((a, b) => b.amount - a.amount)[0];

        // 找到最高门槛的索引
        const highestTierIndex = this.#tiers.findIndex(tier => tier.amount === highestTier.amount);

        // 检查是否在模态框中或元素可见
        const isVisible = this.classList.contains('is-visible') || !this.#modalElement;

        if (isVisible) {
          // 如果元素可见，立即播放动画并更新索引
          this.#lastAnimatedTierIndex = highestTierIndex;
          setTimeout(() => {
            this.#playGoalAnimation(highestTier.type);
          }, 500); // 延迟播放，让进度条动画先完成
        } else {
          // 如果元素不可见（弹框未打开），不更新索引，等待弹框打开时播放
          console.log('Element not visible, animation will be played when modal opens');
        }
      }
    } catch (error) {
      console.error('Error in checkAndPlayTierAnimations:', error);
      // 确保错误不影响其他功能
    }
  }

  // 保持原有的模态框事件处理
  #handleModalEvents(event) {
    if (event.type === 'on:modal:opened') {
      this.classList.add('is-visible');

      // 模态框打开后检查是否需要播放动画
      this.#checkModalAnimation();
    } else if (event.type === 'on:modal:closed') {
      this.classList.remove('is-visible');
    }
  }

  // 新增：检查模态框中的动画播放
  #checkModalAnimation() {
    // 使用多次检查确保数据已更新
    const checkAndPlay = (attempt = 0) => {
      try {
        // 强制重新计算进度
        this.#calculateProgress();

        // 检查是否有达成的门槛需要播放动画
        if (this.#tiers.length > 0) {
          // 只有当当前达到的门槛比上次播放动画的门槛更高时，才播放动画
          if (this.#currentTierIndex > this.#lastAnimatedTierIndex) {
            const currentTier = this.#tiers[this.#currentTierIndex];

            // 更新最后播放动画的门槛索引
            this.#lastAnimatedTierIndex = this.#currentTierIndex;

            // 确保动画元素已准备好
            this.#ensureAnimationReady(() => {
              this.#playGoalAnimation(currentTier.type);
            });
            return;
          }
        }

        // 如果还没有找到需要播放的动画，且尝试次数少于3次，则重试
        if (attempt < 2) {
          setTimeout(() => checkAndPlay(attempt + 1), 200);
        }
      } catch (error) {
        console.error('Error in modal animation check:', error);
      }
    };

    // 延迟执行，给模态框时间完全打开
    setTimeout(() => checkAndPlay(), 100);
  }

  // 新增：确保动画准备就绪
  #ensureAnimationReady(callback) {
    const checkReady = (attempt = 0) => {
      if (this.goalAnimation && this.goalAnimation.isLoaded) {
        callback();
        return;
      }

      if (attempt < 10) {
        // 最多等待1秒
        setTimeout(() => checkReady(attempt + 1), 100);
      } else {
        console.warn('Animation not ready after 1 second, skipping');
      }
    };

    checkReady();
  }
}

customElements.define('free-shipping-bar', FreeShippingBar);
